import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { WasmModuleApiService } from '../../shared/services/wasm/wasm-module-api/wasm-module-api.service';
import { ProjectWasmModule } from '../../shared/interfaces/wasm-module.interface';
import {
  WasmModuleDetailDialogComponent,
  WasmModuleDetailDialogData,
} from '../wasm-module-detail-dialog/wasm-module-detail-dialog.component';

@Component({
  selector: 'app-wasm-modules',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './wasm-modules.component.html',
  styleUrls: ['./wasm-modules.component.scss'],
})
export class WasmModulesComponent {
  @Input() set projectId(value: number | null) {
    this._projectId.set(value);
    if (value) {
      this.loadModules();
    }
  }

  @Output() deleteModule = new EventEmitter<ProjectWasmModule>();

  private readonly wasmModuleApi = inject(WasmModuleApiService);
  private readonly dialog = inject(MatDialog);

  private readonly _projectId = signal<number | null>(null);

  readonly modules = signal<ProjectWasmModule[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  readonly displayedColumns = ['name', 'version', 'description', 'functions', 'actions'];
  readonly isEmpty = computed(() => this.modules().length === 0 && !this.loading());

  loadModules(): void {
    const projectId = this._projectId();
    if (!projectId) return;

    this.loading.set(true);
    this.error.set(null);

    this.wasmModuleApi.listModules(projectId).subscribe({
      next: (modules) => {
        this.modules.set(modules);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.error?.message ?? 'Failed to load modules');
        this.loading.set(false);
      },
    });
  }

  functionCount(module: ProjectWasmModule): number {
    return module.manifest?.functions?.length ?? 0;
  }

  viewDetails(module: ProjectWasmModule): void {
    const data: WasmModuleDetailDialogData = { module };
    this.dialog.open(WasmModuleDetailDialogComponent, {
      data,
      width: '720px',
      maxHeight: '80vh',
    });
  }

  confirmDelete(module: ProjectWasmModule): void {
    const projectId = this._projectId();
    if (!projectId) return;

    this.wasmModuleApi.deleteModule(projectId, module.moduleId).subscribe({
      next: () => {
        this.modules.update((prev) => prev.filter((m) => m.moduleId !== module.moduleId));
        this.deleteModule.emit(module);
      },
      error: (err) => {
        this.error.set(err?.error?.message ?? 'Failed to delete module');
      },
    });
  }
}
